import useApiRequest from "@/lib/hooks/use-request";
import {
  CreateDealRequestDto,
  DealDetailsResponseDto,
  DealsCreationEntitiesResponseDto,
  DealsPipelineSelectableEntityResponseDto,
} from "@/lib/interfaces/deals";
import { Paginated, Pagination } from "@/lib/interfaces/utils";
import { Button } from "@/ui-components/ui/button";
import DataFetchParent from "@/ui-components/ui/data-fetch";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import LinkedItemManager from "@/ui-components/ui/linked-item-manager";
import { Textarea } from "@/ui-components/ui/textarea";
import { zodResolver } from "@hookform/resolvers/zod";
import { Crosshair } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { ControllerRenderProps, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";
import PipelineEditor from "../pipelines/pipeline-editor";

interface Preview {
  title?: string;
  id?: string;
}

const DealsEditor = ({ pipelineId }: { pipelineId?: string }) => {
  const navigate = useNavigate();
  const { apiRequest } = useApiRequest();
  const [dealCreationEntities, setDealCreationEntities] = useState<DealsCreationEntitiesResponseDto>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDealCreationEntities = async () => {
      const res = await apiRequest<DealsCreationEntitiesResponseDto>("deals/creation-entities", "GET");
      setIsLoading(false);
      if (res.data) {
        setDealCreationEntities(res.data);
        const selectedPipeline = res.data.pipelines.find((pipeline) => pipeline._id === pipelineId);
        form.setValue("pipeline", { 
          id: selectedPipeline?._id || "", // Provide empty string as fallback
          title: selectedPipeline?.value || "" // Provide empty string as fallback
        });
      }
    };
    fetchDealCreationEntities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRequest, pipelineId]);

  const dealSchema = z.object({
    title: z
      .string()
      .min(1, { message: "Titel ist erforderlich" })
      .max(100, { message: "Titel darf nicht mehr als 100 Zeichen lang sein" }),
    pipeline: z.object({
      title: z.string().optional(),
      id: z.string().min(1, { message: "Pipeline wird benötigt" }),
    }),
    information: z.string().optional(),
  });

  type DealFormSchema = z.infer<typeof dealSchema>;

  const form = useForm<DealFormSchema>({
    resolver: zodResolver(dealSchema),
    defaultValues: {
      title: "",
      pipeline: {
        id: pipelineId || "",
        title: "",
      },
      information: "",
    },
  });

  const onSubmit = async (data: DealFormSchema) => {
    const req: CreateDealRequestDto = {
      title: data.title,
      belongsToPipelineId: data.pipeline.id || "",
      information: data.information,
    };

    const res = await apiRequest<DealDetailsResponseDto>("deals", "POST", {
      body: req,
      toast: { toastText: "Deal created successfully" },
    });

    if (res.data) {
      navigate(`/deals/${res.data._id}`);
    }
  };

  const onRemove = async (field: ControllerRenderProps<any, any>) => {
    field.onChange(undefined);
  };

  const onAssign = async (
    itemPreview: Preview,
    field: ControllerRenderProps<any, any>
  ) => {
      field.onChange(itemPreview);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5 h-full">
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="flex flex-col gap-2">
              <FormLabel className="font-medium">Titel:*</FormLabel>
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input {...field} placeholder="Title" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col gap-2">
              <FormLabel className="font-medium">Information:</FormLabel>
              <FormField
                control={form.control}
                name="information"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea {...field} placeholder="Additional information about the deal" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex flex-col gap-2">
              <FormLabel className="font-medium">Pipeline:*</FormLabel>
              <FormField
                control={form.control}
                name="pipeline"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <DataFetchParent
                        data={dealCreationEntities}
                        isLoading={isLoading}
                        renderElement={(pipelines: DealsCreationEntitiesResponseDto) => (
                          <PipelineAssigner
                            pipelines={pipelines.pipelines}
                            item={field.value}
                            onAssign={async (pipeline) => {
                              onAssign(pipeline, field);
                            }}
                            onRemove={async () => {
                              onRemove(field);
                            }}
                          />
                        )}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
        <Button type="submit" variant="default" className="text-white font-bold flex items-center gap-4 w-fit self-end">
          <Crosshair className="h-4 w-4" />
          <span>Deal erstellen</span>
        </Button>
      </form>
    </Form>
  );
};

const PipelineAssigner = ({
  item,
  onAssign,
  onRemove,
  pipelines,
}: {
  item?: Preview;
  onAssign: (pipeline: Preview) => Promise<void>;
  onRemove: () => Promise<void>;
  pipelines: DealsPipelineSelectableEntityResponseDto[];
}) => {
  const fetchSearchResults = useCallback(
    async (
      pagination: Pagination,
      searchText: string
    ): Promise<Paginated<DealsPipelineSelectableEntityResponseDto>> => {
      try {
        const searchedPipelines = pipelines.filter((pipeline) =>
          pipeline.value.toLowerCase().includes(searchText.toLowerCase())
        );

        return {
          docs: searchedPipelines,
          hasNextPage: false,
          limit: pagination.pageSize,
          offset: pagination.pageIndex * pagination.pageSize,
          page: pagination.pageIndex,
          hasPrevPage: pagination.pageIndex > 0,
          count: searchedPipelines.length,
          pageCount: 1,
        };
      } catch (error) {
        console.error("Error fetching pipelines:", error);
        return {
          docs: [],
          hasNextPage: false,
          limit: pagination.pageSize,
          offset: pagination.pageIndex * pagination.pageSize,
          page: pagination.pageIndex,
          hasPrevPage: pagination.pageIndex > 0,
          count: 0,
          pageCount: 0,
        };
      }
    },
    [pipelines]
  );

  const renderSearchOption = useCallback(
    (pipeline: DealsPipelineSelectableEntityResponseDto) => ({
      label: pipeline.value,
      value: pipeline._id,
    }),
    []
  );

  return (
    <LinkedItemManager<DealsPipelineSelectableEntityResponseDto>
      editor={<PipelineEditor withoutRedirect={true} />}
      fetchSearchResults={fetchSearchResults}
      renderSearchOption={renderSearchOption}
      customOnSubmit={async (id, title) => {
        await onAssign({ id, title });
      }}
      removeItem={async () => onRemove()}
      link={
        item && item.id && item.title
          ? {
              title: item.title,
              href: `/pipelines/${item.id}`,
              id: item.id,
            }
          : undefined
      }
      title="Pipeline"
    />
  );
};

export default DealsEditor;
